import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTranscript } from "../../../shared/api/transcriptsApi";
import { LoadedTranscript } from "../../../shared/interfaces/Transcript";
import {
  transcriptsCjiCacheOptions,
  transcriptsErrorMessage,
  transcriptsKeys,
} from "./transcriptsQueries";

interface UseTranscriptResult {
  loaded: LoadedTranscript | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Loads the transcript document head on react-query. The return shape is
 * unchanged from the hand-rolled version; react-query's per-key AbortSignal
 * replaces the old manual AbortController (an unmounted or superseded fetch
 * is cancelled and never commits).
 *
 * Both `loaded` and `error` are returned side by side, as before: a failed
 * refetch keeps the last-good document while surfacing the error, and the
 * page checks its loading/error branches before rendering from `loaded`.
 * Cache options are the CJI-conservative set (gcTime 0 in particular: the
 * editor re-initializes its draft store from `loaded`, so a remount must
 * fetch fresh rather than replay a cached document that a background refetch
 * would then clobber).
 */
export const useTranscript = (
  evidenceId: string | undefined,
): UseTranscriptResult => {
  const query = useQuery({
    queryKey: transcriptsKeys.detail(evidenceId),
    queryFn: ({ signal }) => {
      if (!evidenceId) throw new Error("evidenceId is required");
      return getTranscript(evidenceId, signal);
    },
    enabled: !!evidenceId,
    ...transcriptsCjiCacheOptions,
  });

  const { refetch: queryRefetch } = query;
  const refetch = useCallback(() => {
    // Preserve the old hook's no-op without an evidenceId — v5's refetch()
    // would fetch even a disabled query.
    if (evidenceId) void queryRefetch();
  }, [evidenceId, queryRefetch]);

  return {
    loaded: query.data ?? null,
    // isFetching, not isPending: the old flag was true during the manual
    // error-banner retry too, and stays false while disabled.
    isLoading: query.isFetching,
    error: query.error
      ? transcriptsErrorMessage(query.error, "Failed to load transcript")
      : null,
    refetch,
  };
};
